import { useState, useEffect } from 'react';
import { LoadingButton } from '../ui/LoadingSpinner';
import { handleApiError } from '../../utils/errorHandler';
import OmButton from '../ui/OmButton';
import OmTextField from '../ui/OmTextField';
import OmAlert from '../ui/OmAlert';

const ApprovalModal = ({ 
  show, 
  onClose, 
  onApprove, 
  isApproving,
  approvalDecision,
  setApprovalDecision,
  approvalNotes,
  setApprovalNotes,
  rejectionReason,
  setRejectionReason,
  claim
}) => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Reset error and success when modal opens/closes
  useEffect(() => {
    if (show) {
      setError(null);
      setSuccess(false);
    }
  }, [show]);

  // Reset error when form changes
  useEffect(() => {
    if (error) setError(null);
  }, [approvalDecision, approvalNotes, rejectionReason, error]);

  if (!show) return null;

  const handleApprove = async () => {
    if (!approvalDecision) {
      setError('Please select a decision (Approve or Reject)');
      return;
    }

    if (!approvalNotes || approvalNotes.trim().length < 10) {
      setError('Please provide detailed approval notes (minimum 10 characters)');
      return;
    }

    if (approvalDecision === 'REJECT' && (!rejectionReason || rejectionReason.trim().length < 5)) {
      setError('Please provide a specific rejection reason (minimum 5 characters)');
      return;
    }

    try {
      await onApprove();
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const errorInfo = handleApiError(err);
      setError(errorInfo.message);
    }
  };

  const decisionColor = approvalDecision === 'APPROVE' ? 'fresh-leaf' : 'error';

  return (
    <div className="fixed inset-0 bg-carbon-black bg-opacity-60 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-0 border border-steel/20 w-[500px] shadow-depth bg-crystal-white">
        <div className="card-header">
          <h3 className="text-lg font-display font-semibold text-crystal-white">
            Final Approval Decision
          </h3>
        </div>
        
        <div className="card-body space-y-6">
          {error && (
            <OmAlert
              alertType="error"
              message={error}
              showActions={false}
            />
          )}

          {success && (
            <OmAlert
              alertType="success"
              message="Approval decision processed successfully!"
              showActions={false}
            />
          )}

          {/* Claim Summary */}
          <div className="bg-gray-50 border border-gray-200 p-4 rounded">
            <h4 className="text-sm font-semibold text-deep-jungle mb-2">Claim Summary</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="font-medium">Claim #:</span> {claim?.id}</div>
              <div><span className="font-medium">Type:</span> {claim?.type}</div>
              <div><span className="font-medium">Claimant:</span> {claim?.claimantName}</div>
              <div><span className="font-medium">Amount:</span> {claim?.benefitAmount ? `GHS ${claim.benefitAmount.toLocaleString()}` : 'N/A'}</div>
            </div>
            {claim?.recommendationNotes && (
              <div className="mt-3 pt-3 border-t border-gray-300">
                <span className="font-medium text-sm">Officer's Recommendation:</span>
                <p className="text-sm text-gray-700 mt-1">{claim.recommendationNotes}</p>
              </div>
            )}
          </div>

          {/* Decision Selection */}
          <div>
            <label className="block text-sm font-medium text-deep-jungle mb-3">
              Approval Decision <span className="text-error">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <OmButton
                type="button"
                onClick={() => setApprovalDecision('APPROVE')}
                buttonType={approvalDecision === 'APPROVE' ? 'filled-button' : 'outline-button'}
                buttonSize="large"
                leadingIcon="check"
                disabled={isApproving || success}
                className={`p-4 text-center transition-all duration-200 ${
                  approvalDecision === 'APPROVE'
                    ? 'border-fresh-leaf bg-fresh-leaf/10 text-forest-fern'
                    : 'border-steel/30 hover:border-fresh-leaf/50 text-graphite'
                }`}
              >
                <div className="font-semibold">Approve</div>
                <div className="text-xs">Claim meets all criteria</div>
              </OmButton>
              
              <OmButton
                type="button"
                onClick={() => setApprovalDecision('REJECT')}
                buttonType={approvalDecision === 'REJECT' ? 'filled-button' : 'outline-button'}
                buttonSize="large"
                leadingIcon="close"
                disabled={isApproving || success}
                className={`p-4 text-center transition-all duration-200 ${
                  approvalDecision === 'REJECT'
                    ? 'border-error bg-error/10 text-error'
                    : 'border-steel/30 hover:border-error/50 text-graphite'
                }`}
              >
                <div className="font-semibold">Reject</div>
                <div className="text-xs">Claim does not meet criteria</div>
              </OmButton>
            </div>
          </div>

          {/* Approval Notes */}
          <div>
            <OmTextField
              label="Approval Notes *"
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              rows={4}
              placeholder="Provide detailed notes explaining your approval decision..."
              disabled={isApproving || success}
              required
              customValidationMessage={
                approvalNotes.length > 0 && approvalNotes.trim().length < 10
                  ? "Minimum 10 characters required for detailed notes"
                  : !approvalNotes.trim()
                    ? "Approval notes are required"
                    : ""
              }
              alwaysShowValidationMessage={!!(
                (!approvalNotes.trim() || approvalNotes.trim().length < 10)
              )}
              supportingText={`Characters: ${approvalNotes.length} / 10 minimum`}
            />
          </div>

          {/* Rejection Reason (only if rejecting) */}
          {approvalDecision === 'REJECT' && (
            <div>
              <OmTextField
                label="Rejection Reason *"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                placeholder="Provide specific reason(s) for rejection..."
                disabled={isApproving || success}
                required
                customValidationMessage={
                  rejectionReason.length > 0 && rejectionReason.trim().length < 5
                    ? "Minimum 5 characters required for rejection reason"
                    : !rejectionReason.trim()
                      ? "Rejection reason is required"
                      : ""
                }
                alwaysShowValidationMessage={!!(
                  (!rejectionReason.trim() || rejectionReason.trim().length < 5)
                )}
                supportingText={`Characters: ${rejectionReason.length} / 5 minimum`}
              />
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-steel/10">
            <OmButton
              onClick={onClose}
              buttonType="outline-button"
              buttonSize="medium"
              disabled={isApproving || success}
            >
              Cancel
            </OmButton>
            <LoadingButton
              onClick={handleApprove}
              isLoading={isApproving}
              disabled={
                !approvalDecision ||
                !approvalNotes || 
                approvalNotes.trim().length < 10 ||
                (approvalDecision === 'REJECT' && (!rejectionReason || rejectionReason.trim().length < 5)) ||
                isApproving || 
                success
              }
              className={`btn-${decisionColor} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isApproving ? 'Processing...' : `${approvalDecision === 'APPROVE' ? 'Approve' : 'Reject'} Claim`}
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalModal;
